import { Link } from "react-router-dom";
import { ShieldCheck, Inbox } from "lucide-react";
import { label } from "@/lib/api";

export const Logo = ({ className = "h-9", to = "/" }) => (
  <Link to={to} data-testid="brand-logo-link" className="inline-flex items-center shrink-0">
    <img src="/logo.png" alt="Nirogyam Diagnostics & Healthcare" className={`${className} w-auto object-contain`} />
  </Link>
);

const TONES = {
  confirmed: "bg-[#086B2D]/10 text-[#086B2D]", completed: "bg-[#086477]/10 text-[#086477]", in_progress: "bg-[#2412B5]/10 text-[#2412B5]",
  pending_payment: "bg-[#FF9700]/15 text-[#9A5B00]", pending: "bg-[#FF9700]/15 text-[#9A5B00]", more_info: "bg-[#FF9700]/15 text-[#9A5B00]",
  refund_pending: "bg-[#FF9700]/15 text-[#9A5B00]", pending_manual: "bg-[#FF9700]/15 text-[#9A5B00]", processing: "bg-[#FF9700]/15 text-[#9A5B00]",
  cancelled: "bg-red-50 text-red-700", rejected: "bg-red-50 text-red-700", suspended: "bg-red-50 text-red-700", failed: "bg-red-50 text-red-700",
  expired: "bg-slate-100 text-slate-600", no_show: "bg-slate-100 text-slate-600", draft: "bg-amber-50 text-amber-800",
  approved: "bg-[#086B2D]/10 text-[#086B2D]", paid: "bg-[#086B2D]/10 text-[#086B2D]", captured: "bg-[#086B2D]/10 text-[#086B2D]",
  processed: "bg-[#086B2D]/10 text-[#086B2D]", final: "bg-[#086B2D]/10 text-[#086B2D]", active: "bg-[#086B2D]/10 text-[#086B2D]",
  open: "bg-[#2412B5]/10 text-[#2412B5]", resolved: "bg-[#086477]/10 text-[#086477]",
};

export const StatusBadge = ({ status, testId }) => (
  <span data-testid={testId || `status-${status}`} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ${TONES[status] || "bg-slate-100 text-slate-600"}`}>
    {label(status)}
  </span>
);

export const VerifiedBadge = () => (
  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#086B2D]" data-testid="verified-badge">
    <ShieldCheck className="h-3.5 w-3.5" /> Verified
  </span>
);

export const SampleBadge = () => (
  <span className="inline-flex rounded-full border border-[#FF9700]/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#9A5B00]" data-testid="sample-badge">
    Sample profile
  </span>
);

export const EmptyState = ({ title, text, action, icon: Icon = Inbox, testId = "empty-state" }) => (
  <div data-testid={testId} className="surface-soft border-dashed p-8 text-center">
    <Icon className="mx-auto h-6 w-6 text-[#657581]" />
    <p className="mt-3 font-medium text-[#17232E]">{title}</p>
    {text && <p className="mt-1 text-sm muted">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const PageHead = ({ eyebrow, title, sub, right }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
    <div>
      {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
      <h1 className="page-title" data-testid="page-title">{title}</h1>
      {sub && <p className="mt-2 text-sm muted max-w-2xl">{sub}</p>}
    </div>
    {right}
  </div>
);

export const Stat = ({ label: l, value, hint, testId }) => (
  <div className="surface p-5" data-testid={testId}>
    <p className="text-xs muted">{l}</p>
    <p className="mt-2 text-2xl font-semibold font-display text-[#17232E]">{value}</p>
    {hint && <p className="mt-1 text-[11px] muted">{hint}</p>}
  </div>
);

export const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2 text-sm border-b border-[#E2E8ED] last:border-0">
    <span className="muted">{k}</span>
    <span className="text-right font-medium text-[#17232E]">{v ?? "-"}</span>
  </div>
);

const DOC_CATS = [["clinician", "Clinician", "All clinical specialities – diagnose & treat, issue prescriptions"], ["diagnostician", "Diagnostician", "Pathology, microbiology, biochemistry, lab medicine or radiology – explain reports"]];
const DIAG_AREAS = [["lab", "Lab reports (pathology, microbiology, biochemistry, lab medicine)"], ["imaging", "Imaging (radiology)"]];

export function DoctorCategoryPicker({ value, area, onChange, disabled }) {
  return (
    <div className="space-y-2" data-testid="doctor-category-picker">
      <div className="grid sm:grid-cols-2 gap-2">{DOC_CATS.map(([v, l, d]) => (
        <button key={v} type="button" disabled={disabled} onClick={() => onChange(v, v === "diagnostician" ? area || "lab" : null)} data-testid={`doctor-category-${v}`}
          className={`text-left rounded-xl border p-3 transition-colors ${value === v ? "border-[#2412B5] bg-[#2412B5]/[0.05]" : "border-[#E2E8ED]"}`}>
          <p className="text-sm font-semibold">{l}</p><p className="text-xs muted mt-0.5">{d}</p></button>))}</div>
      {value === "diagnostician" && <div className="flex flex-col gap-1.5">{DIAG_AREAS.map(([v, l]) => (
        <label key={v} className="flex items-center gap-2 text-sm"><input type="radio" name="diag_area" disabled={disabled} checked={area === v} onChange={() => onChange("diagnostician", v)} data-testid={`doctor-diag-area-${v}`} />{l}</label>))}</div>}
    </div>
  );
}
